import {Decimal} from "decimal.js";

import * as utils from "@client/utils";

import {formatSeconds} from "@client/forms/segtime/machine";
import {server} from "@reactivated";

export type SetOutput = server.journal.workouts.ExerciseSet.output;
export type SetType = SetOutput["type"];

export type DistanceType = NonNullable<SetOutput["distance_type"]>;
export type SpeedType = NonNullable<SetOutput["speed_type"]>;

// A cardio segment is measured either by the clock (a duration) or by a distance
// covered. The pair of work/rest segments in an interval block each pick their
// own, so a 400m "On" can sit above a 1:30 "Off".
export type Measure = "TIME" | "DISTANCE";

// The white rounded card every cardio block (steady reading, warmup, repeat,
// cooldown) sits in. Borderless to match the journal cards — the off-white page
// background is what sets them apart.
export const CARD = "rounded-xl bg-white";

export const DISTANCE_LABELS: Record<DistanceType, string> = {
    INCHES: "in",
    YARDS: "yd",
    FEET: "ft",
    METERS: "m",
    MILES: "mi",
    KILOMETERS: "km",
};

// The distance units worth offering for cardio — drop inches/feet.
export const CARDIO_DISTANCE_UNITS: DistanceType[] = [
    "METERS",
    "KILOMETERS",
    "MILES",
    "YARDS",
];

export const defaultDistanceType = (isImperial: boolean): DistanceType =>
    isImperial ? "MILES" : "KILOMETERS";

// Speed and pace follow the distance unit's system: miles read as mph and a
// "/mi" pace, everything else as km/h and "/km".
export const speedTypeFor = (distanceType: DistanceType): SpeedType =>
    distanceType === "MILES" ? "MPH" : "KPH";

export const speedLabel = (speedType: SpeedType) =>
    speedType === "MPH" ? "mph" : "km/h";

export const paceUnit = (speedType: SpeedType) => (speedType === "MPH" ? "/mi" : "/km");

// A segment reads as a distance only once it has a distance and no clock time.
export const deriveMeasure = (set: SetOutput | null): Measure =>
    set != null && set.duration == null && set.distance != null ? "DISTANCE" : "TIME";

export const makeCardioSet = (
    isImperial: boolean,
    type: SetType,
    duration: SecondsValue | null,
    isTemplate: boolean,
): SetOutput => {
    const distanceType = defaultDistanceType(isImperial);

    return {
        uuid: crypto.randomUUID(),
        type,
        instructions: "",
        weight_type: null,
        weight_percentage: null,
        progression: null,
        distance_type: distanceType,
        duration_type: null,
        speed_type: speedTypeFor(distanceType),
        duration,
        distance: null,
        speed: null,
        incline: null,
        resistance: null,
        reps: null,
        rpe: null,
        weight: null,
        // Cardio has no per-set completion — a logged segment is just done. In a
        // template the set is a blueprint, not performed work, so it stays
        // incomplete like every other template set.
        status: isTemplate ? "INCOMPLETE" : "COMPLETE",
        attributes: [],
    };
};

// The default a fresh interval block seeds with: a 5-minute warmup, four rounds
// of 60s on / 60s off, then a 5-minute cooldown.
export const seedIntervalSets = (
    isImperial: boolean,
    isTemplate: boolean,
): SetOutput[] => [
    makeCardioSet(isImperial, "WARM_UP", 300, isTemplate),
    ...Array.from({length: 4}).flatMap(() => [
        makeCardioSet(isImperial, "WORKING", 60, isTemplate),
        makeCardioSet(isImperial, "REST", 60, isTemplate),
    ]),
    makeCardioSet(isImperial, "COOL_DOWN", 300, isTemplate),
];

// A single open-ended steady set. Speed stays unit-less until the user enters
// one, matching the rest of the steady form which derives its unit on demand.
export const makeSteadySet = (isImperial: boolean, isTemplate: boolean): SetOutput => ({
    ...makeCardioSet(isImperial, "WORKING", null, isTemplate),
    speed_type: null,
});

type SummarySet = Pick<SetOutput, "type" | "duration" | "distance" | "distance_type">;

const segmentSummary = (set: SummarySet): string =>
    set.duration == null && set.distance != null
        ? `${utils.removeTrailingZeros(set.distance)}${DISTANCE_LABELS[set.distance_type ?? "METERS"]}`
        : formatSeconds(set.duration ?? 0);

// A one-line description of a cardio exercise for read-only previews: an interval
// reads "5:00 WU · 4 × (400m / 1:30) · 5:00 CD"; a steady effort reads its
// duration and distance.
export function cardioSummary(sets: ReadonlyArray<SummarySet>): string {
    const warmup = sets.find((set) => set.type === "WARM_UP") ?? null;
    const cooldown = sets.find((set) => set.type === "COOL_DOWN") ?? null;
    const work = sets.find((set) => set.type === "WORKING") ?? null;
    const rest = sets.find((set) => set.type === "REST") ?? null;
    const rounds = Math.max(
        sets.filter((set) => set.type === "WORKING").length,
        sets.filter((set) => set.type === "REST").length,
    );

    if (warmup != null || cooldown != null || rest != null || rounds > 1) {
        return [
            warmup != null ? `${segmentSummary(warmup)} WU` : null,
            work != null && rest != null
                ? `${rounds} × (${segmentSummary(work)} / ${segmentSummary(rest)})`
                : null,
            cooldown != null ? `${segmentSummary(cooldown)} CD` : null,
        ]
            .filter((part) => part != null)
            .join("  ·  ");
    }

    if (work == null) {
        return "";
    }

    return [
        work.duration != null ? formatSeconds(work.duration) : null,
        work.distance != null
            ? `${utils.removeTrailingZeros(work.distance)}${DISTANCE_LABELS[work.distance_type ?? "METERS"]}`
            : null,
    ]
        .filter((part) => part != null)
        .join(" · ");
}

// Meters per one unit of distance. Canonical base for all conversions.
const METERS_PER: Record<DistanceType, string> = {
    INCHES: "0.0254",
    FEET: "0.3048",
    YARDS: "0.9144",
    METERS: "1",
    KILOMETERS: "1000",
    MILES: "1609.344",
};

// A speed unit is distance-per-hour; this is the distance half of it.
const SPEED_DISTANCE_UNIT: Record<SpeedType, DistanceType> = {
    MPH: "MILES",
    KPH: "KILOMETERS",
};

const toMeters = (value: Decimal, unit: DistanceType) => value.times(METERS_PER[unit]);

const fromMeters = (meters: Decimal, unit: DistanceType) =>
    meters.dividedBy(METERS_PER[unit]);

const parsePositive = (value: string | null): Decimal | null => {
    if (value == null || value === "") {
        return null;
    }

    try {
        const decimal = new Decimal(value);
        return decimal.isFinite() && decimal.greaterThan(0) ? decimal : null;
    } catch {
        return null;
    }
};

// Distance expressed in the speed unit's own distance (miles for mph, km for kph).
const distanceInSpeedUnit = (
    distance: Decimal,
    distanceType: DistanceType,
    speedType: SpeedType,
) => fromMeters(toMeters(distance, distanceType), SPEED_DISTANCE_UNIT[speedType]);

// Pace = time per unit distance, in seconds (a duration). The speed unit picks
// the distance unit: MPH -> seconds per mile, KPH -> seconds per km.
export function computePaceSeconds(input: {
    durationSeconds: SecondsValue | null;
    distance: DecimalValue | null;
    distanceType: DistanceType;
    speedType: SpeedType;
}): SecondsValue | null {
    const distance = parsePositive(input.distance);

    if (distance == null || input.durationSeconds == null) {
        return null;
    }

    if (input.durationSeconds <= 0) {
        return null;
    }

    const distanceInUnit = distanceInSpeedUnit(
        distance,
        input.distanceType,
        input.speedType,
    );

    return new Decimal(input.durationSeconds)
        .dividedBy(distanceInUnit)
        .toDecimalPlaces(0)
        .toNumber();
}
